import { useMemo } from "react";

import useClientPreferences from "/imports/hooks/useClientPreferences";
import { getServiceDurationOptions, resolvePreferredSportService, resolvePreferredLocationId } from "/imports/ui/components/booking";

/**
 * The client's saved preferences on the lessons page: the "Use my preferences" shortcut,
 * and the trainer options (which also name a preferred trainer before slots load).
 * Empty for guests; never offered while a lesson is being moved.
 */
export default function useLessonPreferences({
  slug,
  lessonServices,
  availableSports,
  availableTrainers,
  serviceLocations,
  locations,
  filters,
  setFilters,
  reschedule
}) {
  // The active client's saved preferences — drives both the "Suggested for you"
  // section and the "Use my preferences" filter shortcut. Empty for guests.
  const preferences = useClientPreferences(slug);

  // The Sport + Service the "Use my preferences" button would select, limited to
  // what's offered here. The preferred trainer is applied too when it's loaded
  // and bookable (trainers only load once a duration is chosen).
  const preferredSelection = useMemo(
    () =>
      resolvePreferredSportService({
        sports: preferences.sports,
        services: preferences.services,
        availableSports,
        categoryServices: lessonServices,
        currentSport: filters.sport,
        currentService: filters.serviceId
      }),
    [preferences.sports, preferences.services, availableSports, lessonServices, filters.sport, filters.serviceId]
  );
  // The trainer to apply. availableTrainers only loads once a duration is chosen,
  // so before that we fall back to the client's first preferred trainer (rather
  // than resolving to none) — the availability fetch then scopes to that trainer.
  const preferredTrainer = useMemo(
    () => preferences.trainers.find((pt) => availableTrainers.some((t) => t._id === pt.trainerId)) || preferences.trainers[0] || null,
    [preferences.trainers, availableTrainers]
  );
  // The preferred location to apply, restricted to what the currently selected
  // service is offered at — drives whether the button shows on location alone.
  const preferredLocationId = useMemo(
    () => resolvePreferredLocationId(preferences.locations, serviceLocations),
    [preferences.locations, serviceLocations]
  );
  const canApplyPreferences =
    !reschedule.active && preferences.isAuthenticated && (preferredSelection.hasPreference || !!preferredTrainer || !!preferredLocationId);

  // Snap Sport + Service + Trainer (+ preferred Location) to the client's
  // preferences. Duration: auto-apply when the preferred service offers exactly
  // one option; with several, leave it for the client. Location is resolved
  // against the service being applied and only set when bookable there, so the
  // coherence effect never clears it.
  const applyPreferences = () => {
    const svc = lessonServices.find((s) => s._id === preferredSelection.serviceId) || null;
    const durs = getServiceDurationOptions(svc);
    const duration = durs.length === 1 ? String(durs[0].value) : filters.duration || "";
    const targetLocations = svc?.locationIds?.length ? (locations || []).filter((l) => svc.locationIds.includes(l._id)) : locations || [];
    const locationId = resolvePreferredLocationId(preferences.locations, targetLocations);
    const next = {
      sport: preferredSelection.sportId,
      serviceId: preferredSelection.serviceId,
      trainerId: preferredTrainer?.trainerId || null,
      duration
    };
    if (locationId) next.locationId = locationId;
    setFilters(next);
  };

  // Trainer options for the filter dropdown: the availability-derived trainers
  // plus, when a trainer is selected that hasn't loaded yet (e.g. just applied
  // from preferences before slots loaded), an entry from the preference so its
  // name shows instead of a blank field.
  const trainerOptions = useMemo(() => {
    const opts = availableTrainers.map((t) => ({ _id: t._id, name: t.name }));
    if (filters.trainerId && !opts.some((t) => t._id === filters.trainerId)) {
      const pref = preferences.trainers.find((pt) => pt.trainerId === filters.trainerId);
      if (pref) opts.push({ _id: pref.trainerId, name: pref.trainerName });
    }
    return opts;
  }, [availableTrainers, filters.trainerId, preferences.trainers]);

  return { preferences, canApplyPreferences, applyPreferences, trainerOptions };
}
